import { Controller } from "@hotwired/stimulus"

const MAX_PRESTACIONES = 3

export default class extends Controller {
  static targets = [
    "amount",
    "displayAmount",
    "finalTotal",
    "form",
    "selectedCount",
    "totalAmount",
    "preInvoiceCheckbox",
    "balanceCheckbox",
    "partialAmountInput",
    "partialInputContainer",
    "anularFormContainer",
    "anularForm",
    "anularPreInvoiceId"
  ]

  connect() {
    this.calculate()
    this.preInvoiceAmounts = this.extractPreInvoiceAmounts()
  }

  togglePartialPayment(event) {
    const preInvoiceId = event.target.dataset.preInvoiceId
    const paymentType = event.target.value
    const partialInputDiv = this.getPartialInputContainer(preInvoiceId)
    const partialInput = this.getPartialAmountInput(preInvoiceId)
    
    if (!partialInputDiv || !partialInput) return
    
    if (paymentType === "partial") {
      partialInputDiv.classList.remove("hidden")
      partialInput.required = true
    } else {
      partialInputDiv.classList.add("hidden")
      partialInput.value = ""
      partialInput.required = false
    }
    
    this.calculate()
  }

  extractPreInvoiceAmounts() {
    const amounts = {}
    
    // montos prefacturas
    this.preInvoiceCheckboxTargets?.forEach(checkbox => {
      const preInvoiceId = checkbox.value
      const amount = parseFloat(checkbox.dataset.amount) || 0
      amounts[`pre_invoice_${preInvoiceId}`] = amount
    })
    
    // balance cuenta corriente
    if (this.hasBalanceCheckboxTarget) {
      amounts["balance"] = parseFloat(this.balanceCheckboxTarget.dataset.amount) || 0
    }
    
    return amounts
  }

  updateSelectedInvoices(event) {
    const selected = this.getSelectedPreInvoiceCheckboxes()
    if (event?.target?.type === "checkbox" && selected.length > MAX_PRESTACIONES) {
      event.target.checked = false
      alert(`Máximo ${MAX_PRESTACIONES} prestaciones por comprobante (triplicado en A4). Genere otro comprobante para el resto.`)
    }
    this.calculate()
  }

  handleSubmit(event) {
    event.preventDefault()
    
    if (!this.validateSelection()) return false
    if (!this.validatePartialPayments()) return false

    this.showPreview()
    return false
  }

  validateSelection() {
    if (!this.hasSelectedCountTarget || !this.hasTotalAmountTarget) return true

    const selectedPreInvoices = this.getSelectedPreInvoiceCheckboxes()
    const balanceSelected = this.hasBalanceCheckboxTarget && this.balanceCheckboxTarget.checked

    if (selectedPreInvoices.length === 0 && !balanceSelected) {
      alert("Debe seleccionar al menos una prefactura para cobrar")
      return false
    }

    if (selectedPreInvoices.length > MAX_PRESTACIONES) {
      alert(`Máximo ${MAX_PRESTACIONES} prestaciones por comprobante (triplicado en A4). Genere otro comprobante para el resto.`)
      return false
    }

    return true
  }

  validatePartialPayments() {
    const selectedPreInvoices = this.getSelectedPreInvoiceCheckboxes()
    
    // filtrar pagos parciales
    const partialPayments = selectedPreInvoices
      .filter(checkbox => this.isPartialPayment(checkbox.value))
      .map(checkbox => ({
        id: checkbox.value,
        input: this.getPartialAmountInput(checkbox.value)
      }))
      .filter(payment => payment.input !== null)
    
    // validar pagos parciales
    const invalidPayment = partialPayments.find(payment => {
      const amount = parseFloat(payment.input.value) || 0
      const maxAmount = parseFloat(payment.input.dataset.maxAmount) || 0
      
      if (amount <= 0) {
        alert("El monto del pago parcial debe ser mayor a 0")
        payment.input.focus()
        return true
      }
      
      if (amount > maxAmount) {
        alert(`El monto del pago parcial no puede superar el monto pendiente: ${this.formatCurrency(maxAmount)}`)
        payment.input.focus()
        return true
      }
      
      return false
    })
    
    return !invalidPayment
  }

  showPreview() {
    const event = new CustomEvent("show-invoice-preview", {
      detail: { form: this.formTarget }
    })
    document.dispatchEvent(event)
  }

  getSelectedPreInvoiceCheckboxes() {
    if (!this.hasPreInvoiceCheckboxTarget) return []
    return this.preInvoiceCheckboxTargets.filter(checkbox => checkbox.checked)
  }

  isPartialPayment(preInvoiceId) {
    const partialRadio = this.element.querySelector(`input[name="payment_type_${preInvoiceId}"][value="partial"]`)
    return partialRadio && partialRadio.checked
  }

  getPartialAmountInput(preInvoiceId) {
    return this.partialAmountInputTargets?.find(input => 
      input.dataset.preInvoiceId === preInvoiceId.toString()
    )
  }

  getPartialInputContainer(preInvoiceId) {
    return this.partialInputContainerTargets?.find(container =>
      container.dataset.preInvoiceId === preInvoiceId.toString()
    )
  }

  calculate() {
    if (this.hasSelectedCountTarget && this.hasTotalAmountTarget) {
      this.calculateMultiple()
    } 
    else if (this.hasAmountTarget) {
      this.calculateSingle()
    }
  }

  calculateMultiple() {
    const selectedPreInvoices = this.getSelectedPreInvoiceCheckboxes()
    const balanceSelected = this.hasBalanceCheckboxTarget && this.balanceCheckboxTarget.checked
    const selectedCount = selectedPreInvoices.length + (balanceSelected ? 1 : 0)
    
    // total prefactura seleccionadas
    const preInvoicesTotal = selectedPreInvoices.reduce((total, checkbox) => {
      return total + this.getPreInvoiceAmount(checkbox.value)
    }, 0)
    
    // agregar balance si está seleccionado
    const balanceAmount = balanceSelected ? (this.preInvoiceAmounts["balance"] || 0) : 0
    const totalAmount = preInvoicesTotal + balanceAmount
    
    this.updateSummaryDisplay(selectedCount, totalAmount, totalAmount)
  }

  getPreInvoiceAmount(preInvoiceId) {
    const isPartial = this.isPartialPayment(preInvoiceId)
    
    if (isPartial) {
      const partialInput = this.getPartialAmountInput(preInvoiceId)
      if (!partialInput) return 0
      
      const partialAmount = parseFloat(partialInput.value) || 0
      const maxAmount = parseFloat(partialInput.dataset.maxAmount) || 0
      
      // corregir si excede el máximo
      if (partialAmount > maxAmount) {
        partialInput.value = maxAmount
        return maxAmount
      }
      
      return partialAmount
    }
    
    // retornar monto total
    return this.preInvoiceAmounts[`pre_invoice_${preInvoiceId}`] || 0
  }

  updateSummaryDisplay(selectedCount, totalAmount, finalTotal) {
    if (this.hasSelectedCountTarget) {
      this.selectedCountTarget.textContent = selectedCount
    }
    
    if (this.hasTotalAmountTarget) {
      this.totalAmountTarget.textContent = this.formatCurrency(totalAmount)
    }
    
    if (this.hasFinalTotalTarget) {
      this.finalTotalTarget.textContent = this.formatCurrency(finalTotal)
    }

    this.toggleAnularButton()
  }

  toggleAnularButton() {
    if (!this.hasAnularFormContainerTarget) return

    const selectedPreInvoices = this.getSelectedPreInvoiceCheckboxes()
    const balanceSelected = this.hasBalanceCheckboxTarget && this.balanceCheckboxTarget.checked
    const exactlyOnePrestation = selectedPreInvoices.length === 1 && !balanceSelected
    const selectedIsPendingOnly = exactlyOnePrestation && selectedPreInvoices[0].dataset.pendingOnly === "true"
    const showAnular = exactlyOnePrestation && selectedIsPendingOnly

    this.anularFormContainerTarget.classList.toggle("hidden", !showAnular)
    if (showAnular && this.hasAnularPreInvoiceIdTarget) {
      this.anularPreInvoiceIdTarget.value = selectedPreInvoices[0].value
    }
  }

  submitAnular(event) {
    event.preventDefault()
    if (!confirm("¿Está seguro que desea solicitar la anulación de esta prestación? Se enviará una notificación al profesor para su autorización.")) {
      return
    }
    const anularForm = document.getElementById("anular_pre_invoice_form")
    if (anularForm) anularForm.requestSubmit()
  }

  calculateSingle() {
    const amount = parseFloat(this.amountTarget.value) || 0

    if (this.hasDisplayAmountTarget) {
      this.displayAmountTarget.textContent = this.formatCurrency(amount)
    }
    
    if (this.hasFinalTotalTarget) {
      this.finalTotalTarget.textContent = this.formatCurrency(amount)
    }
  }

  formatCurrency(value) {
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
      minimumFractionDigits: 2
    }).format(value)
  }
}

